import { useState, useRef, useCallback } from 'react';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertTriangle, Loader2, FileUp, X } from 'lucide-react';
import { reportsApi } from '@/services/api';

interface ReconciliationSectionProps {
  projectId: string;
  projectName: string;
}

interface ReconciliationResult {
  summary: {
    total: number;
    matched: number;
    unmatched: number;
    statusBreakdown: {
      completed: number;
      terminated: number;
      quota_full: number;
      security_terminated: number;
      quality_terminated: number;
      invited: number;
      started: number;
    };
  };
  matchedData: any[];
  matchedExternalIds: string[];
  unmatchedIds: string[];
}

type DownloadFormat = 'csv' | 'txt';
type HeaderName = 'TransactionID' | 'transaction_id';
type RespondentStatus =
  | 'completed'
  | 'terminated'
  | 'quota_full'
  | 'security_terminated'
  | 'quality_terminated'
  | 'invited'
  | 'started';

const STATUS_OPTIONS: Array<{ key: RespondentStatus; label: string }> = [
  { key: 'completed', label: 'Completed' },
  { key: 'terminated', label: 'Terminated' },
  { key: 'quota_full', label: 'Quota Full' },
  { key: 'security_terminated', label: 'Security' },
  { key: 'quality_terminated', label: 'Quality' },
  { key: 'invited', label: 'Invited' },
  { key: 'started', label: 'Started' },
];

const ALL_STATUSES: RespondentStatus[] = STATUS_OPTIONS.map(o => o.key);

const ReconciliationSection: React.FC<ReconciliationSectionProps> = ({ projectId, projectName }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Download options state
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>('csv');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [headerName, setHeaderName] = useState<HeaderName>('TransactionID');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  // Which respondent statuses to include in downloads — all checked by default
  // so the default download matches every matched record (current behaviour).
  const [selectedStatuses, setSelectedStatuses] = useState<Set<RespondentStatus>>(
    () => new Set(ALL_STATUSES)
  );

  const toggleStatus = (status: RespondentStatus) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): string | null => {
    const allowedExtensions = ['.csv'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return 'Invalid file type. Please upload a CSV file.';
    }

    if (file.size > maxSize) {
      return 'File too large. Maximum size is 5MB.';
    }

    return null;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  }, []);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setResult(null);
    setError(null);
    setSelectedSupplier('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProcessFile = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setSelectedSupplier('');
    setSelectedStatuses(new Set(ALL_STATUSES));

    try {
      const response = await reportsApi.reconcileProject(projectId, selectedFile);
      
      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError(response.error || 'Failed to process file');
      }
    } catch (err: any) {
      console.error('Reconciliation error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  };

  // Escape a value for a delimited file (quote when it contains the separator/quotes/newlines)
  const formatCell = (value: any): string => {
    const s = value === null || value === undefined ? '' : String(value);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const triggerDownload = (content: string, filename: string, format: DownloadFormat) => {
    const mimeType = format === 'csv'
      ? 'text/csv;charset=utf-8;'
      : 'text/plain;charset=utf-8;';
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // A matched row is included only if its status is currently selected
  const statusSelected = (row: any): boolean =>
    selectedStatuses.has(row['Status'] as RespondentStatus);

  // Master file: every matched ID in one column, supplier name in the second column
  const handleDownloadMaster = () => {
    if (!result) return;
    const rows = result.matchedData.filter(statusSelected);
    if (rows.length === 0) return;

    const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    const filename = `${safeProjectName}_reconciled_master_${date}.${downloadFormat}`;

    const separator = downloadFormat === 'csv' ? ',' : '\t';
    const lines: string[] = [];

    if (downloadFormat === 'csv' && includeHeaders) {
      lines.push([headerName, 'Supplier'].map(formatCell).join(separator));
    }
    for (const row of rows) {
      lines.push([row['Member ID'] ?? '', row['Supplier'] ?? ''].map(formatCell).join(separator));
    }

    triggerDownload(lines.join('\n'), filename, downloadFormat);
  };

  // Per-supplier file: just the IDs for the chosen supplier, in that supplier's required format
  const handleDownloadSupplier = () => {
    if (!result || !selectedSupplier) return;

    const rows = result.matchedData.filter(
      r => (r['Supplier'] || 'N/A') === selectedSupplier && statusSelected(r)
    );
    if (rows.length === 0) return;

    const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const safeSupplier = selectedSupplier.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    const filename = `${safeProjectName}_${safeSupplier}_${date}.${downloadFormat}`;

    const lines: string[] = [];
    if (downloadFormat === 'csv' && includeHeaders) {
      lines.push(headerName);
    }
    for (const row of rows) {
      lines.push(formatCell(row['Member ID'] ?? ''));
    }

    triggerDownload(lines.join('\n'), filename, downloadFormat);
  };

  const handleDownloadUnmatched = () => {
    if (!result || result.unmatchedIds.length === 0) return;
    const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    
    // Create simple CSV with just the unmatched IDs
    const csvContent = ['Original ID', ...result.unmatchedIds].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeProjectName}_reconciled_unmatched_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Matched records limited to the currently selected statuses — drives the
  // download buttons and the per-supplier counts so the UI reflects what will
  // actually be exported.
  const filteredMatched = result ? result.matchedData.filter(statusSelected) : [];

  // Suppliers recognised among the matched records, with their matched ID counts
  const supplierCounts = new Map<string, number>();
  for (const row of filteredMatched) {
    const name = (row['Supplier'] as string) || 'N/A';
    supplierCounts.set(name, (supplierCounts.get(name) || 0) + 1);
  }
  const supplierGroups: Array<[string, number]> = Array.from(supplierCounts.entries()).sort(
    (a, b) => a[0].localeCompare(b[0])
  );

  return (
    <div className="md-card p-6 animate-fade-in-up">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold font-heading text-[#1A1F2B] flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#18A5F9]" />
          Reconciliation
        </h2>
      </div>
      
      <p className="text-sm text-[#9CA3AF] mb-6">
        Upload a CSV file containing IDs to match against respondent records in this project.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Side - Upload */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-[#374151] uppercase tracking-wider">Upload</h3>
          
          {/* Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
              ${isDragging 
                ? 'border-[#18A5F9] bg-[#E8F4FD]' 
                : 'border-[#ECEEF1] hover:border-[#18A5F9]/50 hover:bg-[#F9FAFB]'
              }
              ${selectedFile ? 'bg-[#F9FAFB]' : ''}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {selectedFile ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <FileUp className="w-8 h-8 text-[#18A5F9]" />
                </div>
                <div className="font-semibold text-[#374151]">{selectedFile.name}</div>
                <div className="text-sm text-[#6B7280]">{formatFileSize(selectedFile.size)}</div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFile();
                  }}
                  className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-semibold mt-2"
                >
                  <X className="w-4 h-4" />
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 text-[#9CA3AF] mx-auto" />
                <div className="text-[#374151] font-semibold">
                  Drop CSV file here
                </div>
                <div className="text-sm text-[#9CA3AF]">
                  or click to browse
                </div>
              </div>
            )}
          </div>

          {/* File Info */}
          <div className="text-sm text-[#9CA3AF]">
            Accepted formats: <span className="font-medium">.csv</span> (max 5MB)
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Process Button */}
          <button
            onClick={handleProcessFile}
            disabled={!selectedFile || isProcessing}
            className={`
              w-full md-btn text-[13px] py-3 font-semibold flex items-center justify-center gap-2
              ${!selectedFile || isProcessing 
                ? 'bg-[#ECEEF1] text-[#9CA3AF] cursor-not-allowed' 
                : 'md-btn-primary'
              }
            `}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                Process File
              </>
            )}
          </button>
        </div>

        {/* Right Side - Results & Download */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-[#374151] uppercase tracking-wider">Results</h3>
          
          {result ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="bg-[#F9FAFB] rounded-xl p-4 border border-[#ECEEF1]">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-700">{result.summary.matched}</div>
                      <div className="text-xs text-[#6B7280] font-medium">Matched</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-700">{result.summary.unmatched}</div>
                      <div className="text-xs text-[#6B7280] font-medium">Unmatched</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-[#ECEEF1]">
                  <div className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
                    Status Breakdown
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Completed:</span>
                      <span className="font-semibold text-emerald-600">{result.summary.statusBreakdown.completed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Terminated:</span>
                      <span className="font-semibold text-red-600">{result.summary.statusBreakdown.terminated}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Security:</span>
                      <span className="font-semibold text-orange-600">{result.summary.statusBreakdown.security_terminated}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Quality:</span>
                      <span className="font-semibold text-violet-600">{result.summary.statusBreakdown.quality_terminated ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Quota Full:</span>
                      <span className="font-semibold text-amber-600">{result.summary.statusBreakdown.quota_full}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Invited:</span>
                      <span className="font-semibold text-[#6B7280]">{result.summary.statusBreakdown.invited}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Started:</span>
                      <span className="font-semibold text-blue-600">{result.summary.statusBreakdown.started}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Options */}
              <div className="bg-[#F9FAFB] rounded-xl p-4 border border-[#ECEEF1] space-y-3">
                <div className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider">
                  Download Options
                </div>
                
                {/* Format Selection */}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-[#6B7280] font-medium">Format:</span>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="downloadFormat"
                        value="csv"
                        checked={downloadFormat === 'csv'}
                        onChange={() => setDownloadFormat('csv')}
                        className="w-4 h-4 text-[#18A5F9] focus:ring-[#18A5F9]"
                      />
                      <span className="text-sm text-[#374151]">CSV</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="downloadFormat"
                        value="txt"
                        checked={downloadFormat === 'txt'}
                        onChange={() => setDownloadFormat('txt')}
                        className="w-4 h-4 text-[#18A5F9] focus:ring-[#18A5F9]"
                      />
                      <span className="text-sm text-[#374151]">TXT</span>
                    </label>
                  </div>
                </div>
                
                {/* Header Options (only for CSV) */}
                {downloadFormat === 'csv' && (
                  <div className="space-y-2 pl-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeHeaders}
                        onChange={(e) => setIncludeHeaders(e.target.checked)}
                        className="w-4 h-4 rounded text-[#18A5F9] focus:ring-[#18A5F9]"
                      />
                      <span className="text-sm text-[#374151]">Include header row</span>
                    </label>
                    
                    {includeHeaders && (
                      <div className="flex items-center gap-2 pl-6">
                        <span className="text-sm text-[#6B7280]">Header:</span>
                        <select
                          value={headerName}
                          onChange={(e) => setHeaderName(e.target.value as HeaderName)}
                          className="select-arrow text-sm border border-[#ECEEF1] rounded-lg pl-3 pr-8 py-1.5 bg-white focus:ring-2 focus:ring-[#18A5F9] focus:border-[#18A5F9]"
                        >
                          <option value="TransactionID">TransactionID</option>
                          <option value="transaction_id">transaction_id</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Status Selection — choose which respondent types to export */}
                <div className="space-y-2 pt-1 border-t border-[#ECEEF1]">
                  <span className="text-sm text-[#6B7280] font-medium">Include statuses:</span>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {STATUS_OPTIONS.map(({ key, label }) => (
                      <label key={key} className="flex items-center justify-between gap-2 cursor-pointer">
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedStatuses.has(key)}
                            onChange={() => toggleStatus(key)}
                            className="w-4 h-4 rounded text-[#18A5F9] focus:ring-[#18A5F9]"
                          />
                          <span className="text-sm text-[#374151]">{label}</span>
                        </span>
                        <span className="text-xs text-[#9CA3AF] tabular-nums">
                          {result.summary.statusBreakdown[key]}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-2 pt-1">
                <button
                  onClick={handleDownloadMaster}
                  disabled={filteredMatched.length === 0}
                  className={`
                    w-full md-btn text-[13px] py-3 font-semibold flex items-center justify-center gap-2
                    ${filteredMatched.length === 0
                      ? 'bg-[#ECEEF1] text-[#9CA3AF] cursor-not-allowed'
                      : 'md-btn-primary'
                    }
                  `}
                >
                  <Download className="w-4 h-4" />
                  Download Master ({filteredMatched.length})
                </button>

                {/* Per-supplier export */}
                {supplierGroups.length > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-[#ECEEF1] space-y-2">
                    <div className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider">
                      Download by Supplier
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={selectedSupplier}
                        onChange={(e) => setSelectedSupplier(e.target.value)}
                        className="select-arrow flex-1 text-sm border border-[#ECEEF1] rounded-lg pl-3 pr-8 py-2 bg-white focus:ring-2 focus:ring-[#18A5F9] focus:border-[#18A5F9]"
                      >
                        <option value="">Select supplier…</option>
                        {supplierGroups.map(([name, count]) => (
                          <option key={name} value={name}>
                            {name} ({count})
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleDownloadSupplier}
                        disabled={!selectedSupplier}
                        className={`
                          md-btn text-[13px] px-4 py-2 font-semibold flex items-center justify-center gap-2 whitespace-nowrap
                          ${!selectedSupplier
                            ? 'bg-[#ECEEF1] text-[#9CA3AF] cursor-not-allowed'
                            : 'md-btn-primary'
                          }
                        `}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                    <div className="text-xs text-[#9CA3AF]">
                      Exports only the selected supplier's IDs. Choose <span className="font-semibold">TXT</span> above for Cint, <span className="font-semibold">CSV</span> for other suppliers.
                    </div>
                  </div>
                )}

                <button
                  onClick={handleDownloadUnmatched}
                  disabled={result.unmatchedIds.length === 0}
                  className={`
                    w-full md-btn text-[13px] py-3 font-semibold flex items-center justify-center gap-2
                    ${result.unmatchedIds.length === 0 
                      ? 'bg-[#ECEEF1] text-[#9CA3AF] cursor-not-allowed' 
                      : 'md-btn-outlined'
                    }
                  `}
                >
                  <Download className="w-4 h-4" />
                  Download Unmatched ({result.summary.unmatched})
                </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[#F9FAFB] rounded-xl p-8 border border-[#ECEEF1] text-center">
              <div className="text-[#9CA3AF] space-y-2">
                <FileText className="w-10 h-10 mx-auto opacity-50" />
                <div className="font-medium">No results yet</div>
                <div className="text-sm">
                  Upload and process a file to see reconciliation results
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-[#E8F4FD] border border-[#B3DCFA] rounded-xl p-4">
        <p className="text-sm text-[#0B7BC7]">
          <strong>How it works:</strong> Upload a CSV file containing IDs (one per line or in the first column).
          The system matches them against both masked IDs and external panel IDs within this project.
          The <strong>Master</strong> file lists every matched ID alongside its supplier name; use
          <strong> Download by Supplier</strong> to export a single supplier's IDs in its required format (TXT for Cint, CSV for others).
        </p>
      </div>
    </div>
  );
};

export default ReconciliationSection;
